import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import type { CSSProperties } from "react";
import { faqMock } from "../api/mock";
import arrow from "/assets/images/arrow.svg";

/**
 * Компонент секции FAQ (Frequently Asked Questions).
 * Отображает список часто задаваемых вопросов с раскрывающимися ответами.
 * Использует анимацию через framer-motion.
 *
 * @component
 * @returns {ReactNode} JSX-разметка секции FAQ
 */
export const FaqSection = () => {
  const { title, items } = faqMock;
  const [ openIndex, setOpenIndex ] = useState<number | null>(null);
  const headingStyle = {
    "--heading-size-min": "38.4",
    "--heading-size-max": "38.4",
    "--heading-tablet-size": "3.5rem",
    "--heading-mobile-size": "3rem",
    "--heading-margin-desktop": "0",
    "--heading-margin-tablet": "0",
    "--heading-margin-mobile": "0",
  } as CSSProperties;

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id={"faq"} className={"w-full mx-auto flex flex-col gap-16 mobile:gap-6 mt-100 mobile:mt-70"}>
      {/* Заголовок */}
      <h2 style={headingStyle} className={"text-center text-nero"}>
        {title}
      </h2>

      {/* Аккордеон с линиями сверху и снизу */}
      <div className={"flex flex-col border-y border-silver divide-y divide-silver"}>
        {items.map((item, i) => (
          <div key={i} className={"py-6 mobile:py-4"}>
            <button
              onClick={() => toggle(i)}
              className={"w-full flex justify-between items-center text-left cursor-pointer gap-4 mobile:gap-2"}
            >
              <span
                className={`text-[3.5rem] mobile:text-[2.5rem] font-semibold transition-colors duration-200 ${
                  openIndex === i ? "text-cherrywood" : "text-nero"
                }`}
              >
                {item.question}
              </span>

              {/* Кастомная стрелка */}
              <img
                src={arrow}
                alt={"arrow icon"}
                className={`w-8 h-8 transition-transform duration-300 mobile:w-4 mobile:h-4 ${
                  openIndex === i ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>

            {/* Контент */}
            <AnimatePresence initial={false}>
              {openIndex === i && (
                <motion.div
                  key={"content"}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                  className={"overflow-hidden"}
                >
                  <p className={"text-doveGray mt-6 text leading-[1.5]"}>
                    {item.answer}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </section>
  );
};
